import React, { useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
} from '@mui/material';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { authTokenState, cognitoIdState } from 'features/auth/state/authAtoms';
import { sendableMorphingState } from 'features/morphing/state/morphingAtoms';
import {
  allMorphedState,
  displayModelState,
  displayMorphedState,
  fileRenderKeyState,
  S3File
} from 'features/file-browser/state/fileAtoms';
import { stageParameterState } from 'features/morphing/state/stageParameterAtoms';
import { CustomTypography } from 'shared/ui/CustomTypography';
import { CustomButton } from 'shared/ui/CustomButton';
import { submitMorphingStart } from 'features/morphing/api/submitMorphingStart';
import { isVisibleMorphedState } from 'features/three-view/state/threeAtoms';

const iconSx = {
  fontSize: 12,
  color: '#e1e1e1'
};

const boxSx = {
  backgroundColor: '#404040',
  borderRadius: '5px',
  height: '70%',
  overflow: 'auto',
};

const checkboxSx = {
  '& .MuiSvgIcon-root': {
    borderColor: '0.5px solid #e1e1e1',
    '&.Mui-checked': {
      borderColor: '0.5px solid #e1e1e1',
    },
  },
  color: '#e1e1e1',
};

export const MorphingAPIs: React.FC = () => {
  const [displayMorphed, setDisplayMorphed] = useRecoilState(displayMorphedState);
  const [isVisibleMorphed, setisVisibleMorphed] = useRecoilState(isVisibleMorphedState);

  const setRenderKey = useSetRecoilState(fileRenderKeyState);

  const allMorphed = useRecoilValue(allMorphedState);
  const authToken = useRecoilValue(authTokenState);
  const cognitoId = useRecoilValue(cognitoIdState);
  const displayModel = useRecoilValue(displayModelState);
  const stageParameter = useRecoilValue(stageParameterState);
  const sendable = useRecoilValue(sendableMorphingState);

  const [apiLoading, setApiLoading] = useState<boolean>(false);

  const morphingAPIClick = async (morphingType: string) => {
    setApiLoading(true);
    const model = await submitMorphingStart(
      displayModel,
      authToken,
      cognitoId,
      stageParameter,
      morphingType
    );
    if (model) {
      setDisplayMorphed([model]);
      setRenderKey((p: number) => p + 1);
    }
    setApiLoading(false);
  };

  const handleToggle = () => {
    setisVisibleMorphed((prev) => !prev);
  };

  const handleCheckboxChange = (file: S3File, checked: boolean) => {
    if (checked) {
      setDisplayMorphed((prev) => [...prev, file]);
    } else {
      setDisplayMorphed((prev) => prev.filter((f) => f.name !== file.name));
    }
  };

  return (
    <Box component="div" sx={{ height: '100%', boxSizing: 'border-box', padding: 0.5, gap: 0.5 }}>

      <Box component="div" sx={{ display: 'flex', alignItems: 'center' }}>
        <CustomTypography>Morphing APIs</CustomTypography>
      </Box>

      <Divider sx={{ width: '100%', my: 0.5, bgcolor: '#e1e1e1' }} />

      <Box component="div" sx={{ display: 'flex', gap: 0.5 }}>
        <Box component="div" sx={{ flex: 2 }}>
          <CustomButton
            size='small'
            disabled={!sendable || apiLoading}
            onClick={() => morphingAPIClick('lower')}
            loading={apiLoading}
            loadingPosition='start'
            startIcon={<VerticalAlignBottomIcon />}
            variant='contained'
            sx={{ fontSize: '10px', width: '100%' }}
          >
            FFD lower
          </CustomButton>
        </Box >
        <Box component="div" sx={{ flex: 2 }}>
          <CustomButton
            size='small'
            disabled={!sendable || apiLoading}
            onClick={() => morphingAPIClick('upper')}
            loading={apiLoading}
            loadingPosition='start'
            startIcon={<VerticalAlignTopIcon />}
            variant='contained'
            sx={{ fontSize: '10px', width: '100%' }}
          >
            FFD upper
          </CustomButton>
        </Box >
      </Box>

      <Box component="div" sx={{ display: 'flex', alignItems: 'center', paddingTop: 0.5 }}>
        <CustomTypography>Display Morphed Model</CustomTypography>
        <IconButton onClick={handleToggle}>
          {isVisibleMorphed ? <VisibilityIcon sx={iconSx} /> : <VisibilityOffIcon sx={iconSx} />}
        </IconButton>
      </Box>

      <Divider sx={{ width: '100%', my: 0.5, bgcolor: '#e1e1e1' }} />

      <Box component="div" sx={boxSx}>
        {allMorphed.map((file) => (
          <Box key={file.name} component="div" sx={{ display: 'flex', alignItems: 'center', mb: -2 }}>
            <Checkbox
              sx={checkboxSx}
              checked={displayMorphed.some((f) => f.name === file.name)}
              onChange={(e) => handleCheckboxChange(file, e.target.checked)}
            />
            <CustomTypography>{file.name}</CustomTypography>
          </Box>
        ))}
      </Box>

    </Box >
  );
};
